if (!customElements.get('blog-events')) {
  class BlogEvents extends HTMLElement {
    constructor() {
      super();

      this.currentPage = 0;
      this.totalEvents = 0;
      this.loadedCount = 0;
    }

    connectedCallback() {
      // Read sensitive config from embedded JSON (keeps token out of DOM attributes)
      const configEl = this.querySelector('script[data-config]');

      if (configEl) {
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('[BlogEvents] Invalid config JSON', e);
        }
      }

      this.apiBase = this.getAttribute('api-base-url') || '';
      this.apiToken = this.config?.token || '';
      this.eventStatus = this.getAttribute('event-status') || '';
      this.perPage = parseInt(this.getAttribute('articles-per-view'), 10) || 18;

      // Cache DOM refs
      this.grid = this.querySelector('[data-grid]');
      this.template = this.querySelector('template');
      this.stateLoading = this.querySelector('[data-state="loading"]');
      this.stateEmpty = this.querySelector('[data-state="empty"]');
      this.stateError = this.querySelector('[data-state="error"]');
      this.loadMoreEl = this.querySelector('[data-load-more]');
      this.loadMoreText = this.querySelector('[data-load-more-text]');
      this.loadMoreBar = this.querySelector('[data-load-more-bar]');
      this.loadMoreBtn = this.querySelector('[data-load-more-btn]');

      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', () =>
          this.loadPage(this.currentPage + 1),
        );
      }

      this.loadPage(1);
    }

    // ── State management ───────────────────────────────────────────────────

    showState(state) {
      const states = [this.stateLoading, this.stateEmpty, this.stateError];

      states.forEach((el) => {
        if (el) el.hidden = true;
      });

      if (state) state.hidden = false;
    }

    // ── API fetch ──────────────────────────────────────────────────────────

    fetchEvents(page) {
      if (!this.apiBase || !this.apiToken) {
        return Promise.reject(new Error('API not configured'));
      }

      const params = new URLSearchParams({
        page: page,
        total: this.perPage,
        sort: 'dateOccurs',
        sortType: 'ASC',
      });

      if (this.eventStatus) {
        params.set('status', this.eventStatus);
      }

      const url =
        this.apiBase.replace(/\/$/, '') + '/api/events?' + params.toString();

      return fetch(url, {
        headers: { Authorization: 'Bearer ' + this.apiToken },
      }).then((res) => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      });
    }

    // ── Load page ──────────────────────────────────────────────────────────

    loadPage(page) {
      const isFirstPage = page === 1;

      if (isFirstPage) {
        this.showState(this.stateLoading);
        if (this.grid) this.grid.innerHTML = '';
        if (this.loadMoreEl) this.loadMoreEl.hidden = true;
      }

      if (this.loadMoreBtn) {
        this.loadMoreBtn.disabled = true;
        this.loadMoreBtn.textContent = 'Loading…';
      }

      this.fetchEvents(page)
        .then((data) => {
          console.log(data);

          const events = data.events || [];
          this.totalEvents = data.total || events.length;
          this.currentPage = page;

          if (isFirstPage && events.length === 0) {
            this.showState(this.stateEmpty);
            return;
          }

          this.showState(null);
          this.renderEvents(events);
          this.updateLoadMore();
        })
        .catch((err) => {
          console.error('[BlogEvents]', err);

          if (isFirstPage) {
            this.showState(this.stateError);
          }

          this.updateLoadMore();
        });
    }

    // ── Render events ──────────────────────────────────────────────────────

    renderEvents(events) {
      if (!this.template || !this.grid) return;

      const fragment = document.createDocumentFragment();

      events.forEach((ev) => {
        const card = this.template.content.firstElementChild.cloneNode(true);
        this.fillCard(card, ev);
        fragment.appendChild(card);
      });

      this.grid.appendChild(fragment);
      this.loadedCount = this.grid.querySelectorAll('.article-card').length;
    }

    fillCard(card, ev) {
      const date = BlogEvents.formatDate(ev.dateOccurs);
      const locText = BlogEvents.locationLabel(ev);

      // URL — link to the event blog article
      const link = card.querySelector('[data-field="url"]');

      if (link) {
        link.href =
          '/blogs/events/event?eventId=' + encodeURIComponent(ev.eventId);
      }

      // Image
      const img = card.querySelector('[data-field="image"]');

      if (img) {
        if (ev.imageUrl) {
          img.src = ev.imageUrl;
          img.alt = ev.name || '';
        } else {
          img.remove();
        }
      }

      // Points badge
      const points = card.querySelector('[data-field="points"]');

      if (points) {
        if (ev.points > 0) {
          points.textContent = '+' + ev.points + ' pts';
        } else {
          points.remove();
        }
      }

      // Date
      this.setField(card, 'date-day', date.day);
      this.setField(card, 'date-month', date.month);
      this.setField(card, 'time', date.time);

      // Tags
      const tags = card.querySelector('[data-field="tags"]');

      if (tags) {
        if (ev.tags && ev.tags.length) {
          tags.textContent = Array.isArray(ev.tags)
            ? ev.tags.join(', ')
            : ev.tags;
        } else {
          tags.remove();
        }
      }

      // Name
      this.setField(card, 'name', ev.name);

      // Description — strip HTML tags for the card preview
      const desc = card.querySelector('[data-field="description"]');

      if (desc) {
        var plainText = BlogEvents.stripHtml(ev.description);

        if (plainText) {
          desc.textContent = plainText;
        } else {
          desc.remove();
        }
      }

      // Location
      const locationRow = card.querySelector('[data-field="location"]');

      if (locationRow) {
        if (locText) {
          const locationText = locationRow.querySelector(
            '[data-field="location-text"]',
          );
          if (locationText) locationText.textContent = locText;
        } else {
          locationRow.remove();
        }
      }

      // Attendees
      const attendeeRow = card.querySelector('[data-field="attendees"]');

      if (attendeeRow) {
        if (typeof ev.attendeeCount === 'number') {
          const attendeeText = attendeeRow.querySelector(
            '[data-field="attendee-text"]',
          );
          if (attendeeText)
            attendeeText.textContent = ev.attendeeCount + ' attending';
        } else {
          attendeeRow.remove();
        }
      }

      // CTA label
      const cta = card.querySelector('[data-field="cta"]');

      if (cta) {
        const ctaLabel = cta.querySelector('[data-field="cta-label"]');

        if (ctaLabel) {
          ctaLabel.textContent = ev.registrationUrl
            ? 'Register'
            : 'View Details';
        }
      }
    }

    setField(card, name, value) {
      const el = card.querySelector('[data-field="' + name + '"]');
      if (el) el.textContent = value || '';
    }

    // ── Load More ──────────────────────────────────────────────────────────

    updateLoadMore() {
      if (!this.loadMoreEl) return;

      const hasMore = this.loadedCount < this.totalEvents;

      this.loadMoreEl.hidden = !hasMore;

      if (!hasMore) return;

      if (this.loadMoreText) {
        this.loadMoreText.textContent =
          'Showing ' + this.loadedCount + ' of ' + this.totalEvents + ' events';
      }

      if (this.loadMoreBar) {
        const pct =
          this.totalEvents > 0
            ? (this.loadedCount / this.totalEvents) * 100
            : 0;
        this.loadMoreBar.style.setProperty('--fill', pct + '%');
      }

      if (this.loadMoreBtn) {
        this.loadMoreBtn.disabled = false;
        this.loadMoreBtn.textContent = 'Load more';
      }
    }

    // ── Helpers (static) ───────────────────────────────────────────────────

    static formatDate(isoString) {
      try {
        const naive = isoString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '').replace(/\.\d+$/, '');
        const d = new Date(naive + 'Z');
        const tz = 'UTC';

        return {
          day: d.toLocaleString('en-US', { timeZone: tz, day: '2-digit' }),
          month: d.toLocaleString('en-US', { timeZone: tz, month: 'short' }),
          time: d.toLocaleString('en-US', {
            timeZone: tz,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
        };
      } catch (e) {
        return { day: '--', month: '---', time: '' };
      }
    }

    static locationLabel(event) {
      if (event.label) return event.label;

      const loc = event.location;
      if (!loc) return '';

      return [loc.city, loc.state].filter(Boolean).join(', ');
    }

    static stripHtml(html) {
      if (!html) return '';
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    }
  }

  customElements.define('blog-events', BlogEvents);
}
